<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRoute } from 'vue-router';

const modelContacts = defineModel('contacts', { type: Array, default: () => [] });

const route = useRoute();
const accountId = route.params.accountId;

const apiToken = ref('');
const allContacts = ref([]);
const isLoading = ref(false);

const attributeDefinitions = ref([]);
const selectedKey = ref('');
const selectedValue = ref('');

const currentPage = ref(1);
const pageSize = 10;

const fetchApiToken = async () => {
  const res = await fetch(`/api/v1/custom_querys/admin_token?account_id=${accountId}`, {
    headers: { 'custom_access_token': '1a2b3c4d5e' }
  });
  const data = await res.json();
  apiToken.value = data.api_access_token;
};

const fetchAttributes = async () => {
  const res = await fetch(`/api/v1/accounts/${accountId}/custom_attribute_definitions`, {
    headers: { 'api_access_token': apiToken.value }
  });
  attributeDefinitions.value = await res.json();
};

const fetchAllContacts = async () => {
  isLoading.value = true;
  let page = 1;
  const contacts = [];
  let totalExpected = null;

  while (true) {
    const res = await fetch(`/api/v1/accounts/${accountId}/contacts?page=${page}&per_page=100`, {
      headers: { 'api_access_token': apiToken.value }
    });

    const data = await res.json();
    const validContacts = (data.payload || []).filter(c => c.phone_number);
    contacts.push(...validContacts);

    // ✅ Guardamos cuántos contactos esperamos recibir
    if (!totalExpected && data.meta?.count) {
      totalExpected = Number(data.meta.count);
    }

    // ✅ Si ya tenemos todos los que esperamos, paramos
    if (contacts.length >= totalExpected) break;

    page++;
    if (page > 20) break; // protección anti loop infinito
  }

  allContacts.value = contacts;
  isLoading.value = false;
};

const filteredContacts = computed(() => {
  if (!selectedKey.value || !selectedValue.value) return allContacts.value;
  return allContacts.value.filter(c =>
    c.custom_attributes?.[selectedKey.value]?.toLowerCase() === selectedValue.value.toLowerCase()
  );
});

const paginatedContacts = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return filteredContacts.value.slice(start, start + pageSize);
});

const totalPages = computed(() =>
  Math.ceil(filteredContacts.value.length / pageSize)
);

const toggleSelection = (phone) => {
  if (modelContacts.value.includes(phone)) {
    modelContacts.value = modelContacts.value.filter(p => p !== phone);
  } else {
    modelContacts.value.push(phone);
  }
};

const toggleSelectAll = () => {
  const phones = filteredContacts.value.map(c => c.phone_number);
  if (phones.every(p => modelContacts.value.includes(p))) {
    modelContacts.value = modelContacts.value.filter(p => !phones.includes(p));
  } else {
    modelContacts.value = Array.from(new Set([...modelContacts.value, ...phones]));
  }
};

const areAllSelected = computed(() =>
  filteredContacts.value.length > 0 &&
  filteredContacts.value.every(c => modelContacts.value.includes(c.phone_number))
);

watch(apiToken, async () => {
  await fetchAttributes();
  await fetchAllContacts();
});

watch([selectedKey, selectedValue], () => {
  currentPage.value = 1;
});

onMounted(fetchApiToken);
</script>

<template>
  <div class="space-y-4 text-sm text-n-slate-12">
    <!-- Filtros -->
    <div class="flex flex-wrap items-center gap-3">
      <div class="flex gap-2 flex-1">
        <select
          v-model="selectedKey"
          class="w-44 h-10 text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 text-n-slate-12"
        >
          <option disabled value="">Filtrar por atributo</option>
          <option v-for="attr in attributeDefinitions" :key="attr.id" :value="attr.attribute_key">
            {{ attr.attribute_display_name }}
          </option>
        </select>

        <input
          v-model="selectedValue"
          class="flex-1 h-10 text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 text-n-slate-12"
          placeholder="Valor del atributo"
        />
      </div>

      <button
        v-if="filteredContacts.length"
        @click="toggleSelectAll"
        class="bg-primary hover:bg-primary/90 text-white rounded-lg px-4 py-2 text-sm transition"
      >
        {{ areAllSelected ? 'Deseleccionar todos' : 'Seleccionar todos' }}
      </button>
    </div>

    <!-- Contenedor de contactos -->
    <div class="border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 shadow-sm p-4">
      <div v-if="isLoading" class="text-n-slate-10">Cargando contactos...</div>
      <div v-else-if="filteredContacts.length === 0" class="text-n-slate-10">No hay contactos disponibles.</div>

      <div v-else>
        <!-- Lista de contactos -->
        <div class="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          <p>Contactos totales: {{filteredContacts.length}}</p>
          <label
            v-for="contact in paginatedContacts"
            :key="contact.id"
            class="flex items-start gap-3 py-3 text-sm text-n-slate-12"
          >
            <input
              type="checkbox"
              :value="contact.phone_number"
              :checked="modelContacts.includes(contact.phone_number)"
              @change="toggleSelection(contact.phone_number)"
              class="mt-1 accent-primary"
            />
            <div>
              <p class="font-medium">{{ contact.name || 'Sin nombre' }}</p>
              <p class="text-xs text-n-slate-11">{{ contact.phone_number }}</p>
              <div
                v-for="(value, key) in contact.custom_attributes"
                :key="key"
                class="text-xs text-n-slate-10"
              >
                {{ key }}: {{ value }}
              </div>
            </div>
          </label>
        </div>

        <!-- Paginación -->
        <div
          v-if="totalPages > 1"
          class="flex justify-between items-center mt-4 pt-4 border-t border-slate-200 dark:border-slate-700"
        >
          <button
            @click="currentPage--"
            :disabled="currentPage <= 1"
            class="px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Anterior
          </button>

          <div class="text-sm">
            Página {{ currentPage }} de {{ totalPages }}
          </div>

          <button
            @click="currentPage++"
            :disabled="currentPage >= totalPages"
            class="px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
